import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { logUserLogin } from "./analytics";
import { db } from "./firebase";

// Mobile-friendly Info component for tooltips
function MobileInfo({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block", marginLeft: 8 }}>
      <span 
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#ffffff",
          color: "#374151",
          fontSize: 12,
          fontWeight: 600,
          border: "1px solid #e5e7eb",
          cursor: "pointer"
        }}
        onClick={() => setOpen(!open)}
      >i</span>
      {open && (
        <div style={{
          position: "absolute",
          bottom: "130%",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 99999,
          whiteSpace: "normal",
          width: "280px",
          background: "#1f2937",
          color: "#f9fafb",
          border: "1px solid #374151",
          borderRadius: 12,
          padding: "16px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
          fontSize: 13,
          lineHeight: "1.6"
        }}>
          {text}
          <div style={{
            position: "absolute",
            bottom: "-6px",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: "6px solid #1f2937"
          }}></div>
        </div>
      )}
    </span>
  );
}

export default function SignupPage() {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0); // 0 = account info, 1-3 = slideshow steps
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [accountData, setAccountData] = useState({
    name: "",
    email: location.state?.email || "",
    password: "",
    confirmPassword: ""
  });
  const [loanData, setLoanData] = useState({
    totalDebt: "",
    planYears: 15,
    interestRate: "",
    interestUnknown: false
  });
  const [logoWhiteSvg, setLogoWhiteSvg] = useState(null);
  const { signInWithGoogle, createUserWithEmailAndPassword } = useAuth();
  const navigate = useNavigate();

  const totalSteps = 4; // Account info + 3 slideshow steps

  // Logo (white) inline voor scherpe weergave
  useEffect(() => {
    fetch("/logo-name-white.svg")
      .then((r) => r.text())
      .then((text) => {
        const scaled = text.replace(
          /<svg([^>]*)>/,
          (match) => {
            let attrs = match
              .replace(/\swidth="[^"]*"/, ' width="100%"')
              .replace(/\sheight="[^"]*"/, ' height="100%"');
            if (!/width=/.test(attrs)) attrs = attrs.replace(/<svg/, '<svg width="100%"');
            if (!/height=/.test(attrs)) attrs = attrs.replace(/<svg\s/, '<svg height="100%" ');
            return attrs.replace(/>$/, ' preserveAspectRatio="xMidYMid meet" style="display:block">');
          }
        );
        setLogoWhiteSvg(scaled);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location.state]);

  const handleSocialSignup = async (provider) => {
    setIsLoading(true);
    setError("");
    
    try {
      let result;
      switch (provider) {
        case 'google':
          result = await signInWithGoogle();
          break;
        default:
          throw new Error('Unknown provider');
      }
      
      // After Google signup, go straight to the portal
      navigate('/portal');
    } catch (error) {
      console.error('Signup error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Aanmelden geannuleerd');
      } else {
        setError(`Aanmelden mislukt: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    
    if (!accountData.name || !accountData.email) {
      setError("Vul minimaal je naam en e-mailadres in");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountData.email)) {
      setError("Vul een geldig e-mailadres in");
      return;
    }

    if (!accountData.password) {
      setError("Kies een wachtwoord (minimaal 6 tekens)");
      return;
    }
    if (accountData.password.length < 6) {
      setError("Wachtwoord moet minimaal 6 tekens zijn");
      return;
    }
    if (accountData.password !== accountData.confirmPassword) {
      setError("Wachtwoorden komen niet overeen");
      return;
    }
    if (!termsAccepted) {
      setError("Accepteer de algemene voorwaarden om door te gaan");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const newCred = await createUserWithEmailAndPassword(accountData.email, accountData.password);
      
      if (newCred?.user) {
        await newCred.user.updateProfile({
          displayName: accountData.name
        });

        if (db && db.collection) {
          try {
            await db.collection('users').doc(newCred.user.uid).set({
              name: accountData.name,
              email: accountData.email,
              createdAt: new Date().toISOString()
            }, { merge: true });
          } catch (dbError) {
            console.warn('Could not save user data to Firestore:', dbError);
          }
        }

        logUserLogin(newCred.user, 'email');
        navigate('/portal');
      }
    } catch (error) {
      console.error('Signup error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError("Dit e-mailadres is al in gebruik. Probeer in te loggen.");
      } else if (error.code === 'auth/weak-password') {
        setError("Kies een sterker wachtwoord (minimaal 6 tekens).");
      } else {
        setError(error.message || "Aanmelden mislukt");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setAccountData(prev => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  const handleLoanDataChange = (field, value) => {
    setLoanData(prev => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  const handleNext = () => {
    // Validation based on current step
    if (currentStep === 1) {
      // Step 1: Total debt validation
      if (!loanData.totalDebt || parseFloat(loanData.totalDebt) <= 0) {
        setError("Vul een geldig totaal bedrag in");
        return;
      }
      setError("");
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Step 2: Plan years is always set (default 15)
      setError("");
      setCurrentStep(3);
    } else if (currentStep === 3) {
      // Step 3: Interest rate - if unknown, use default based on planYears
      let interestToUse = loanData.interestRate;
      if (loanData.interestUnknown || !loanData.interestRate) {
        // Default rates: 2.21% for 15 years, 2.57% for 35 years
        interestToUse = loanData.planYears === 15 ? "2.21" : "2.57";
      }
      
      // Navigate to portal with all data
      const portalData = {
        totalDebt: parseFloat(loanData.totalDebt),
        planYears: loanData.planYears,
        annualRate: parseFloat(interestToUse) / 100
      };
      
      navigate('/portal', { state: { prefillData: portalData } });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError("");
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'google':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  // Render account info step (step 0)
  const renderAccountStep = () => (
    <>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <h2 style={{ 
          marginTop: 0, 
          marginBottom: 6, 
          fontSize: 24, 
          fontWeight: 700, 
          color: "#1f2937" 
        }}>
          Maak een account
        </h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Vul je gegevens in om te beginnen
        </p>
      </div>

      {error && (
        <div style={{
          padding: 12,
          background: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: 12,
          color: "#dc2626",
          fontSize: 14,
          marginBottom: 24,
          textAlign: "left"
        }}>
          {error}
        </div>
      )}

      {/* Google / Gmail - prominent bovenaan */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => handleSocialSignup('google')}
          disabled={isLoading}
          style={{
            flex: 1,
            padding: "16px",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            background: "#fff",
            color: "#374151",
            fontWeight: 500,
            fontSize: 15,
            cursor: isLoading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
          }}
        >
          {getProviderIcon('google')}
          <span>Aanmelden met Google</span>
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, color: "#9ca3af", fontSize: 14 }}>
        <div style={{ flex: 1, height: 1, background: "#e5e7eb" }}></div>
        <span>Of met e-mail en wachtwoord</span>
        <div style={{ flex: 1, height: 1, background: "#e5e7eb" }}></div>
      </div>

      <form onSubmit={handleAccountSubmit} style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: "block",
            fontSize: 12,
            color: "#9ca3af",
            marginBottom: 6,
            fontWeight: 500
          }}>
            Volledige naam *
          </label>
          <input
            type="text"
            placeholder="Jan Jansen"
            value={accountData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            disabled={isLoading}
            required
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              fontSize: 14,
              boxSizing: "border-box"
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: "block",
            fontSize: 12,
            color: "#9ca3af",
            marginBottom: 6,
            fontWeight: 500
          }}>
            E-mailadres *
          </label>
          <input
            type="email"
            placeholder="voorbeeld@email.nl"
            value={accountData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={isLoading}
            required
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              fontSize: 14,
              boxSizing: "border-box"
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: "block",
            fontSize: 12,
            color: "#9ca3af",
            marginBottom: 6,
            fontWeight: 500
          }}>
            Wachtwoord * (min. 6 tekens)
          </label>
          <input
            type="password"
            placeholder="••••••••"
            value={accountData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            disabled={isLoading}
            autoComplete="new-password"
            minLength={6}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              fontSize: 14,
              boxSizing: "border-box"
            }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{
            display: "block",
            fontSize: 12,
            color: "#9ca3af",
            marginBottom: 6,
            fontWeight: 500
          }}>
            Wachtwoord bevestigen *
          </label>
          <input
            type="password"
            placeholder="••••••••"
            value={accountData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            disabled={isLoading}
            autoComplete="new-password"
            minLength={6}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              fontSize: 14,
              boxSizing: "border-box"
            }}
          />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 20, fontSize: 14, color: "#374151", position: "relative" }}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              opacity: 0,
              width: 18,
              height: 18,
              margin: 0,
              cursor: "pointer"
            }}
          />
          <span
            role="presentation"
            style={{
              width: 18,
              height: 18,
              flexShrink: 0,
              borderRadius: 4,
              border: "2px solid " + (termsAccepted ? "#3399FF" : "#d1d5db"),
              background: termsAccepted ? "#3399FF" : "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none"
            }}
          >
            {termsAccepted && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5L4.5 8.5L11 1.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span>
            Ik accepteer de{" "}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#3399FF", fontWeight: 600, textDecoration: "underline" }}>
              algemene voorwaarden
            </Link>
          </span>
        </label>

        <button
          type="submit"
          disabled={isLoading || !termsAccepted}
          style={{
            width: "100%",
            padding: "16px",
            borderRadius: 12,
            background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
            color: "#ffffff",
            border: "none",
            fontSize: 16,
            fontWeight: 600,
            cursor: isLoading || !termsAccepted ? "not-allowed" : "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
          }}
        >
          {isLoading ? 'Account aanmaken...' : 'Doorgaan'}
        </button>
      </form>

    </>
  );

  // Render slideshow step 1: Total student loan
  const renderStep1 = () => (
    <>
      <div style={{ textAlign: "center", marginBottom: 32, flexShrink: 0 }}>
        <div style={{
          width: 100,
          height: 100,
          margin: "0 auto 20px",
          background: "linear-gradient(135deg, rgba(51,153,255,0.1) 0%, rgba(34,136,221,0.1) 100%)",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 40
        }}>
          💰
        </div>
        <h2 style={{ 
          marginTop: 0, 
          marginBottom: 6, 
          fontSize: 24, 
          fontWeight: 700, 
          color: "#1f2937" 
        }}>
          Wat is je totale studieschuld?
        </h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Vul het totaalbedrag in dat je nog moet aflossen
        </p>
      </div>

      {error && (
        <div style={{
          padding: 12,
          background: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: 12,
          color: "#dc2626",
          fontSize: 14,
          marginBottom: 24,
          textAlign: "left"
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: "auto", flexShrink: 0 }}>
        <label style={{
          display: "block",
          fontSize: 12,
          color: "#9ca3af",
          marginBottom: 6,
          fontWeight: 500
        }}>
          Totaal bedrag (€)
        </label>
        <input
          type="number"
          placeholder="23000"
          value={loanData.totalDebt}
          onChange={(e) => handleLoanDataChange('totalDebt', e.target.value)}
          min="0"
          step="1"
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            background: "#fff",
            color: "#374151",
            fontSize: 16,
            fontWeight: 600,
            boxSizing: "border-box",
            textAlign: "center"
          }}
        />
      </div>

      <button
        onClick={handleNext}
        style={{
          width: "100%",
          padding: "14px",
          borderRadius: 12,
          background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
          color: "#ffffff",
          border: "none",
          fontSize: 15,
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 8px 24px rgba(51,153,255,0.35)",
          marginTop: 20,
          flexShrink: 0
        }}
      >
        Volgende
      </button>
    </>
  );

  // Render slideshow step 2: Repayment plan (15 or 35 years)
  const renderStep2 = () => (
    <>
      <div style={{ textAlign: "center", marginBottom: 28, flexShrink: 0 }}>
        <div style={{
          width: 100,
          height: 100,
          margin: "0 auto 20px",
          background: "linear-gradient(135deg, rgba(51,153,255,0.1) 0%, rgba(34,136,221,0.1) 100%)",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 40
        }}>
          📅
        </div>
        <h2 style={{ 
          marginTop: 0, 
          marginBottom: 6, 
          fontSize: 24, 
          fontWeight: 700, 
          color: "#1f2937" 
        }}>
          Welk aflosstelsel heb je?
        </h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Kies tussen 15 of 35 jaar aflossen
        </p>
      </div>

      <div style={{ marginBottom: "auto", flexShrink: 0 }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          marginBottom: 20 
        }}>
          <label style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#374151"
          }}>
            Aflosstelsel
          </label>
          <MobileInfo text="Je aflosstelsel bepaalt rente en looptijd: 15 jaar (nieuwe regeling) of 35 jaar (oude regeling). Welk stelsel voor jou geldt zie je in Mijn DUO bij 'Mijn studieschuld'/'Aflossen'. Ben je vóór 2023 begonnen met studeren? Dan val je meestal onder 35 jaar; anders vaak 15 jaar. Kies hier het stelsel dat in Mijn DUO staat." />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => handleLoanDataChange('planYears', 15)}
            style={{
              flex: 1,
              padding: "16px",
              borderRadius: 12,
              border: loanData.planYears === 15 ? "2px solid #3399FF" : "2px solid #e5e7eb",
              background: loanData.planYears === 15 ? "rgba(51,153,255,0.1)" : "#fff",
              color: loanData.planYears === 15 ? "#3399FF" : "#374151",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            15 jaar
          </button>
          <button
            onClick={() => handleLoanDataChange('planYears', 35)}
            style={{
              flex: 1,
              padding: "16px",
              borderRadius: 12,
              border: loanData.planYears === 35 ? "2px solid #3399FF" : "2px solid #e5e7eb",
              background: loanData.planYears === 35 ? "rgba(51,153,255,0.1)" : "#fff",
              color: loanData.planYears === 35 ? "#3399FF" : "#374151",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            35 jaar
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 20, flexShrink: 0 }}>
        <button
          onClick={handleBack}
          style={{
            flex: 1,
            padding: "14px",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            background: "#fff",
            color: "#374151",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          Terug
        </button>
        <button
          onClick={handleNext}
          style={{
            flex: 1,
            padding: "14px",
            borderRadius: 12,
            background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
            color: "#ffffff",
            border: "none",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 8px 24px rgba(51,153,255,0.35)"
          }}
        >
          Volgende
        </button>
      </div>
    </>
  );

  // Render slideshow step 3: Interest rate
  const renderStep3 = () => (
    <>
      <div style={{ textAlign: "center", marginBottom: 28, flexShrink: 0 }}>
        <div style={{
          width: 100,
          height: 100,
          margin: "0 auto 20px",
          background: "linear-gradient(135deg, rgba(51,153,255,0.1) 0%, rgba(34,136,221,0.1) 100%)",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 40
        }}>
          📊
        </div>
        <h2 style={{ 
          marginTop: 0, 
          marginBottom: 6, 
          fontSize: 24, 
          fontWeight: 700, 
          color: "#1f2937" 
        }}>
          Wat is je rentepercentage?
        </h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Vul het percentage in of laat het invullen
        </p>
      </div>

      <div style={{ marginBottom: "auto", flexShrink: 0 }}>
        {!loanData.interestUnknown && (
          <div style={{ marginBottom: 12 }}>
            <label style={{
              display: "block",
              fontSize: 12,
              color: "#9ca3af",
              marginBottom: 6,
              fontWeight: 500
            }}>
              Rentepercentage (%)
            </label>
            <input
              type="number"
              placeholder={loanData.planYears === 15 ? "2.21" : "2.57"}
              value={loanData.interestRate}
              onChange={(e) => handleLoanDataChange('interestRate', e.target.value)}
              min="0"
              max="10"
              step="0.01"
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                background: "#fff",
                color: "#374151",
                fontSize: 16,
                fontWeight: 600,
                boxSizing: "border-box",
                textAlign: "center"
              }}
            />
          </div>
        )}

        <button
          onClick={() => {
            handleLoanDataChange('interestUnknown', !loanData.interestUnknown);
            if (!loanData.interestUnknown) {
              handleLoanDataChange('interestRate', '');
            }
          }}
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: 12,
            border: loanData.interestUnknown ? "2px solid #3399FF" : "1px solid #e5e7eb",
            background: loanData.interestUnknown ? "rgba(51,153,255,0.1)" : "#fff",
            color: loanData.interestUnknown ? "#3399FF" : "#374151",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          {loanData.interestUnknown ? "✓ " : ""}Ik weet het niet
        </button>

        {loanData.interestUnknown && (
          <p style={{ 
            marginTop: 10, 
            fontSize: 12, 
            color: "#6b7280", 
            textAlign: "center" 
          }}>
            We vullen automatisch het standaard percentage in ({loanData.planYears === 15 ? "2.21%" : "2.57%"})
          </p>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 20, flexShrink: 0 }}>
        <button
          onClick={handleBack}
          style={{
            flex: 1,
            padding: "14px",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            background: "#fff",
            color: "#374151",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          Terug
        </button>
        <button
          onClick={handleNext}
          style={{
            flex: 1,
            padding: "14px",
            borderRadius: 12,
            background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
            color: "#ffffff",
            border: "none",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 8px 24px rgba(51,153,255,0.35)"
          }}
        >
          Starten
        </button>
      </div>
    </>
  );

  // Determine which step to show (account step shows at step 0, slideshow starts at step 1)
  const renderCurrentStep = () => {
    if (currentStep === 0) return renderAccountStep();
    if (currentStep === 1) return renderStep1();
    if (currentStep === 2) return renderStep2();
    if (currentStep === 3) return renderStep3();
    return null;
  };

  // Calculate active slide for dots (0-3, where 0 is account, 1-3 are slideshow)
  const activeSlideIndex = currentStep === 0 ? 0 : currentStep;

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", position: "relative" }}>
      {/* Header Section */}
      <div style={{
        padding: "32px 20px 100px 20px",
        position: "relative"
      }}>
        {/* Back button and login link */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          marginBottom: 32
        }}>
          <Link to="/" style={{ 
            color: "#fff", 
            textDecoration: "none",
            fontSize: 18,
            fontWeight: 500
          }}>
            ←
          </Link>
          {currentStep === 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#fff", fontSize: 14 }}>Al een account?</span>
              <Link to="/login" style={{
                background: "rgba(255,255,255,0.25)",
                color: "#fff",
                padding: "8px 16px",
                borderRadius: 20,
                textDecoration: "none",
                fontSize: 14,
                fontWeight: 500
              }}>
                Inloggen
              </Link>
            </div>
          )}
        </div>
        
        {/* Brand Name */}
        <div style={{ textAlign: "center", margin: 0 }}>
          {logoWhiteSvg ? (
            <div style={{ height: 48, width: 180, margin: "0 auto", display: "block" }} dangerouslySetInnerHTML={{ __html: logoWhiteSvg }} aria-hidden="true" />
          ) : (
            <img src="/logo-name-white.svg" alt="Slimmer af" style={{ height: 48, width: "auto", display: "block", margin: "0 auto" }} />
          )}
        </div>

        {/* Navigation Dots - Only show during slideshow (steps 1-3) */}
        {currentStep > 0 && (
          <div style={{ 
            display: "flex", 
            justifyContent: "center", 
            gap: 8,
            alignItems: "center"
          }}>
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                style={{
                  width: step === currentStep ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: step === currentStep ? "#fff" : "rgba(255,255,255,0.4)",
                  transition: "all 0.3s ease"
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* White Card - narrow, centered, with space on both sides */}
      <div style={{ padding: "0 clamp(16px, 5vw, 32px)", position: "relative", zIndex: 1 }}>
        <div style={{
          background: "#fff",
          borderRadius: 28,
          marginTop: "-56px",
          marginLeft: "auto",
          marginRight: "auto",
          width: "100%",
          maxWidth: 440,
          padding: "40px 24px",
          position: "relative",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            overflow: "hidden",
            minHeight: 0,
            flex: "0 0 auto"
          }}>
            {renderCurrentStep()}
          </div>
        </div>
      </div>
      {/* Spacer so there is always blue gradient visible below the card on mobile */}
      <div style={{ minHeight: 80 }} aria-hidden="true" />
    </div>
  );
}
